import { TRAINING } from './config';
import { round2 } from './math';
import type { WorkoutSet } from './types';

/**
 * 1RM estimado por la fórmula de Epley: `peso × (1 + reps/30)` (SPEC 7.2).
 *
 * Es una estimación, no una medida: cuanto más alto el número de repeticiones,
 * más se aleja de la realidad. Sirve para comparar una sesión con la anterior,
 * que es justo para lo que la usa el motor de progresión.
 */
export function epley1Rm(weightKg: number, reps: number): number | null {
  if (!(weightKg > 0) || !(reps > 0)) return null;
  return round2(weightKg * (1 + reps / 30));
}

/**
 * Serie efectiva: no es de calentamiento y se hizo con RIR ≤ 3 (SPEC 7.4).
 * Es la unidad con la que se cuenta el volumen real de entrenamiento.
 */
export function isEffectiveSet(set: WorkoutSet): boolean {
  return !set.warmup && set.rir <= TRAINING.effectiveSetMaxRir;
}

/** Mejor 1RM estimado entre las series efectivas. */
export function bestOneRm(sets: readonly WorkoutSet[]): number | null {
  let best: number | null = null;

  for (const set of sets) {
    if (!isEffectiveSet(set)) continue;
    const estimate = epley1Rm(set.weightKg, set.reps);
    if (estimate != null && (best == null || estimate > best)) best = estimate;
  }

  return best;
}

/** Volumen de una sesión: series efectivas × repeticiones × peso. */
export function volumeKg(sets: readonly WorkoutSet[]): number {
  return round2(sets.filter(isEffectiveSet).reduce((total, s) => total + s.reps * s.weightKg, 0));
}
